import { useNavigate, useParams } from "react-router-dom";
import MainContainer from "../../layout/MainContainer";
import CategoryList from "../../features/manageCategories/categoryList";
import CategoryTopbar from "../../features/manageCategories/topbar";
import AddIcon from "@mui/icons-material/Add";
import { useEffect, useState } from "react";
import { Box, Button, IconButton, Switch, Tooltip } from "@mui/material";
import TableComponent from "../../components/TableComponent";
import { GridColDef } from "@mui/x-data-grid";
import AddCategory from "../../Modals/addCategory";
import AddSubCategory from "../../Modals/addSubCategory";
import SearchBar from "../../components/SearchBar";
import AddService from "../../Modals/addService";
import {
  useAddServicesMutation,
  useLazyDeleteServicesQuery,
  useUpdateServicesMutation,
  useLazyGetServicesQuery,
} from "../../services/services";
import { showError, showToast } from "../../constants/toasts";
import moment from "moment";
import { Service } from "../../types/General";
import { ConfirmationModal } from "../../Modals/confirmationModal";
import ServiceDetails from "../../Modals/serviceDetails";
import { isValidInput } from "../../utils/validations";
import { getFromStorage } from "../../helpers/storage";
import { STORAGE_KEYS } from "../../constants/storageKeys";
import useTranslation from "../../hooks/traslation";
import Loader from "../../constants/Loader";
import { useSensors } from "@dnd-kit/core";

interface ServiceRow extends Service {
  _id: string;
  id: string;
  status: boolean;
  createdAt: string;
}

const ServiceList = () => {
  const navigate = useNavigate();
  const { id, cateId } = useParams();

  const [open, setOpen] = useState(false);
  const [getServices] = useLazyGetServicesQuery();
  const [addService] = useAddServicesMutation();
  const [updateStatus] = useUpdateServicesMutation();
  const [deleteService] = useLazyDeleteServicesQuery();
  const [updateService] = useUpdateServicesMutation();
  const [isStatusLoading, setIsStatusLoading] = useState(false);
  const [error, setError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isPageLoading, setIsPageLoading] = useState(true);
  const [isFormLoading, setIsFormLoading] = useState(false);
  const [rows, setRows] = useState<ServiceRow[]>([]);
  const [price, setPrice] = useState<string>("");
  const [name, setName] = useState<string>("");
  const [name_ar, setName_ar] = useState<string>("");
  const [selectedId, setSelectedId] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const [description_ar, setDescription_ar] = useState<string>("");
  const [openDelete, setOpenDelete] = useState(false);
  const [openDetails, setOpenDetails] = useState(false);
  const [page, setPage] = useState<number>(1);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>("");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedService, setSelectedService] = useState<string[]>([]);
  const translate = useTranslation();
  const [totalCount, setTotalCount] = useState(10);

  const handleCloseModal = () => {
    setOpen(false);
  };

  const onPageChange = (newPage: number) => {
    setPage(newPage);
  };
  let totalPages = Math.ceil(totalCount / 10);

  const getAllServices = async () => {
    setIsLoading(true);
    setIsPageLoading(true);
    try {
      const response = await getServices({
        id: id || "",
        page,
        search: debouncedSearchTerm,
        limit: 10,
      }).unwrap();
      if (response?.statusCode === 200) {
        setRows(
          response?.data?.list?.map((data: { _id: string }) => ({
            ...data,
            id: data?._id,
          })) || []
        );
        setTotalCount(response?.data?.totalCount || 10);
      }
    } catch (error) {
      showError(translate?.auth?.failedToLoad);
    } finally {
      setIsLoading(false);
      setIsPageLoading(false);
    }
  };
  const handleStatusChange = async (id: string, status: boolean) => {
    setIsStatusLoading(true);
    try {
      const service = rows.find((row) => row._id === id);
      if (!service) {
        throw new Error(translate?.auth?.serviceNotFound);
      }

      const body = {
        subCategoryId: service.subCategoryId,
        name: service.name,
        price: service.price,
        description: service.description,
        status: status,
      };

      const response = await updateStatus({
        id: id,
        body: body,
      }).unwrap();

      if (response.statusCode === 200) {
        showToast(
          status
            ? translate?.auth?.enabledService
            : translate?.auth?.disabledService
        );
        setRows((prevRows) =>
          prevRows.map((row) => (row._id === id ? { ...row, status } : row))
        );
      } else {
        showError(translate?.auth?.faildeServiceStatus);
      }
    } catch (error: any) {
      showError(error?.data?.message || translate?.auth?.faildeServiceStatus);
    } finally {
      setIsStatusLoading(false);
    }
  };

  const handleSubmit = async () => {
    setError(true);
    setIsFormLoading(true);
    const body = {
      name: name,
      subCategoryId: selectedService || [],
      price: Number(price),
      description: description,
      name_ar: name_ar,
      ...(description ? { description: description } : {}),
      ...(description_ar ? { description_ar: description_ar } : {}),
    };
    if (!description.length || !description_ar.length) {
      showError("description is not allow to be empty");
      setIsFormLoading(false);
      return;
    }

    if (selectedId) {
      try {
        const response = await updateService({
          body,
          id: selectedId,
        }).unwrap();
        if (response?.statusCode === 200) {
          showToast(translate?.auth?.succesfullServiceUpdate);
          setOpen(false);
        }
      } catch (error: any) {
        console.log("error: ", error);
      } finally {
        setIsFormLoading(false);
        getAllServices();
      }
    } else {
      try {
        const response = await addService({ data: body }).unwrap();
        if (response?.statusCode === 200) {
          showToast(translate?.auth?.addService);
          setOpen(false);
          setName("");
          setPrice("");
          setDescription("");
        }
      } catch (error: any) {
        console.log("error: ", error);
      } finally {
        setIsFormLoading(false);
        getAllServices();
      }
    }
  };
  const handleDelete = async () => {
    setIsLoading(true);
    try {
      const response = await deleteService({ id: selectedId }).unwrap();
      setIsLoading(false);

      if (response?.statusCode === 200) {
        showToast(translate?.auth?.deleteService);
        if (rows.length === 1 && page > 1) {
          setPage(page - 1);
        } else {
          getAllServices();
        }
      }
    } catch (error: any) {
      showError(error?.data?.message || "");
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getAllServices();
  }, [page, debouncedSearchTerm]);

  const label = { inputProps: { "aria-label": "Switch demo" } };
  const getLang = getFromStorage(STORAGE_KEYS.language);

  const columns: GridColDef<(typeof rows)[number]>[] = [
    {
      flex: 2,
      minWidth: 220,
      field: "name",
      headerName: translate?.main?.servicesName,
      editable: false,
      sortable: false,
      renderCell: ({ row }: any) => {
        return (
          <div className="user_block">
            <p>{row?.name || "-"}</p>
          </div>
        );
      },
    },
    {
      flex: 2,
      minWidth: 220,
      field: "price",
      headerName: translate?.main?.startingPrice,
      editable: false,
      sortable: false,
      renderCell: ({ row }: { row: ServiceRow }) => {
        return (
          <div className="user_block">
            <p>BHD {row?.price}</p>
          </div>
        );
      },
    },
    {
      flex: 1,
      minWidth: 180,
      field: "createdAt",
      headerName: translate?.main?.createdDate,
      editable: false,
      sortable: false,
      renderCell: ({ row }: { row: { createdAt: string } }) => {
        return (
          <div className="user_block">
            <p>{moment(row?.createdAt).format("DD MMM YYYY")}</p>
          </div>
        );
      },
    },

    {
      flex: 1,
      minWidth: 100,
      field: "status",
      headerName: translate?.globals?.status,
      editable: false,
      sortable: false,
      align: "center",
      headerAlign: "center",
      renderCell: ({ row }) => {
        return (
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Switch
              {...label}
              size="small"
              checked={row?.status}
              onChange={() => handleStatusChange(row?._id, !row?.status)}
              disabled={isLoading}
            />
          </Box>
        );
      },
    },
    {
      minWidth: 130,
      field: "action",
      headerName: translate?.globals?.actions,
      editable: false,
      sortable: false,
      renderCell: ({ row }: { row: ServiceRow }) => {
        return (
          <div className="table_actions v2">
            <Box>
              <Tooltip title="View">
                <IconButton
                  onClick={() => {
                    setOpenDetails(true);
                    setSelectedId(row?._id);
                    setDescription(row?.description);
                    setName(row?.name);
                    setPrice(row?.price.toString());
                  }}
                >
                  <img src="/static/images/eye_icon.svg" alt="" />
                </IconButton>
              </Tooltip>
            </Box>
            <Box>
              <Tooltip title="Edit">
                <IconButton
                  onClick={() => {
                    setSelectedId(row?._id);
                    setName(row?.name);
                    setDescription(row?.description);
                    setPrice(row?.price.toString());
                    setOpen(true);
                  }}
                >
                  <img src="/static/images/edit2_icon.svg" alt="" />
                </IconButton>
              </Tooltip>
            </Box>
            <Box>
              <Tooltip title="Delete">
                <IconButton
                  onClick={() => {
                    setSelectedId(row?._id);
                    setOpenDelete(true);
                  }}
                >
                  <img src="/static/images/trash2_icon.svg" alt="" />
                </IconButton>
              </Tooltip>
            </Box>
          </div>
        );
      },
    },
  ];
  let hidden = ["fullName"];

  return (
    <MainContainer isLoading={isPageLoading}>
      <div className="main_title">
        <h1>
          {translate?.main?.Manage} {translate?.main?.AdOns}
        </h1>

        <p>
          <span onClick={() => navigate("/dashboard")}>
            {translate?.dashboard?.dashboard}
          </span>{" "}
          -{" "}
          <span onClick={() => navigate("/manage-categories")}>
            {translate?.main?.manageCategories}
          </span>
          <span
            onClick={() => navigate(`/manage-categories/subcategory/${cateId}`)}
          >
            {" "}
            - {translate?.main?.subServices}
          </span>
          - {translate?.main?.AddOns}
        </p>
      </div>

      <div className="cards">
        <Loader isLoading={isFormLoading} />
        <div className="table_header">
          <div className="left_s">
            <Box className="search_box">
              <SearchBar
                placeholder={translate?.user?.SearchHere}
                searchTerm={searchTerm}
                setDebouncedSearchTerm={setDebouncedSearchTerm}
                value={searchTerm}
                onCross={() => setSearchTerm("")}
                onChange={(val: any) => {
                  if (isValidInput(val.target.value)) {
                    setSearchTerm(val.target.value);
                  }
                }}
              />
            </Box>
          </div>
          <div className="right_s">
            <Button
              className="btn btn_primary"
              onClick={() => {
                setName("");
                setPrice("");
                setOpen(true);
                setName_ar("");
                setSelectedId("");
                setDescription("");
                setDescription_ar("");
                setSelectedService([]);
                setError(false);
              }}
            >
              <AddIcon /> {translate?.main?.add} {translate?.main?.AddOns}
            </Button>
          </div>
        </div>
        {/* 
        <h2>Category Name: Plumber</h2> */}

        <div className="sorting_table">
          <TableComponent
            columns={columns?.filter((item) => !hidden.includes(item.field))}
            rows={rows}
            page={page}
            onPageChange={onPageChange}
            count={totalCount}
          />
        </div>
      </div>
      <AddService
        open={open}
        cateId={cateId}
        onClose={handleCloseModal}
        setOpen={setOpen}
        handleSubmit={handleSubmit}
        name={name}
        setName={setName}
        price={price}
        setPrice={setPrice}
        description={description}
        selectedId={selectedId}
        setSelectedId={setSelectedId}
        setDescription={setDescription}
        selectedService={selectedService}
        setSelectedService={setSelectedService}
        name_ar={name_ar}
        description_ar={description_ar}
        setName_ar={setName_ar}
        setDescription_ar={setDescription_ar}
      />
      <ServiceDetails
        setOpen={setOpenDetails}
        open={openDetails}
        price={price}
        name={name}
        description={description}
        onClose={handleCloseModal}
        selectedId={selectedId}
        setSelectedId={setSelectedId}
        name_ar={name_ar}
        description_ar={description_ar}
        setName_ar={setName_ar}
        setDescription_ar={setDescription_ar}
      />
      <ConfirmationModal
        text={translate?.user?.deleteConfirmation}
        openModal={openDelete}
        setOpenModal={setOpenDelete}
        handleSubmit={handleDelete}
      />
    </MainContainer>
  );
};

export default ServiceList;
